/*
 * Agent tool registry: tool name -> { kind, run }. executeToolUse() turns one
 * tool_use block into a tool_result block plus an activity record for the
 * chat UI. It never throws: invalid input, refused writes and executor
 * failures all come back as error tool_results so the model can adjust.
 *
 * Writes are planned on a copy first; deletes and edits touching more than
 * CONFIRM_THRESHOLD events wait for the user's approval before they commit.
 */

import { TOOL_SCHEMAS } from "./toolSchemas.js";
import { validateInput } from "./validateInput.js";
import { READ_TOOLS } from "./readTools.js";
import { WRITE_TOOLS, MAX_REVIEW_CHANGES } from "./writeTools.js";
import { NAV_TOOLS } from "./navTools.js";

export const CONFIRM_THRESHOLD = 25;

const REGISTRY = {};
for (const [name, run] of Object.entries(READ_TOOLS)) REGISTRY[name] = { kind: "read", run };
for (const [name, run] of Object.entries(WRITE_TOOLS)) REGISTRY[name] = { kind: "write", run };
for (const [name, run] of Object.entries(NAV_TOOLS)) REGISTRY[name] = { kind: "nav", run };

export const TOOL_NAMES = Object.keys(REGISTRY);

export function getToolKind(name) {
    return REGISTRY[name]?.kind ?? null;
}

/**
 * Deletes always need approval; other writes when they touch more than 25
 * events. Creating a group only adds, so it never asks.
 */
export function needsConfirmation(name, plan) {
    if (name === "create_group") return false;
    return name === "delete_events" || plan.touched > CONFIRM_THRESHOLD;
}

function toolResult(id, content, isError = false) {
    const block = { type: "tool_result", tool_use_id: id, content: String(content) };
    if (isError) block.is_error = true;
    return block;
}

function summarizePlan(plan) {
    const { counts, settings } = plan;
    const parts = [];
    if (counts.updated) parts.push(`updated ${counts.updated}`);
    if (counts.created) parts.push(`created ${counts.created}`);
    if (counts.deleted) parts.push(`deleted ${counts.deleted}`);
    let text = `Group ${plan.groupIndex + 1} "${plan.groupName}": ${parts.length ? `${parts.join(", ")} event(s)` : "no events changed"}`;
    if (settings.length) text += `; settings changed: ${settings.map((s) => `${s.field} ${JSON.stringify(s.before)} -> ${JSON.stringify(s.after)}`).join(", ")}`;
    text += ".";
    const numbers = plan.changes.filter((c) => c.type !== "deleted").map((c) => `#${c.number}`);
    if (numbers.length) {
        text += ` Changed event numbers now: ${numbers.slice(0, 50).join(", ")}${numbers.length > 50 ? ", …" : ""}.`;
    }
    if (plan.info) text += ` ${plan.info}`;
    return text;
}

function writeActivity(name, plan) {
    return {
        kind: name,
        groupIndex: plan.groupIndex,
        groupId: plan.groupId,
        groupName: plan.groupName,
        counts: plan.counts,
        touched: plan.touched,
        settings: plan.settings,
        changes: plan.changes.slice(0, MAX_REVIEW_CHANGES),
    };
}

/**
 * Run one tool_use block.
 * @param {{ id: string, name: string, input: object }} block
 * @param {object} ctx  store adapter (see agentContext in assistantStore)
 * @param {{ confirm: (request: { name: string, activity: object }) => Promise<boolean> }} hooks
 * @returns {Promise<{ result: object, activity: object }>}
 */
export async function executeToolUse(block, ctx, { confirm }) {
    const base = { toolUseId: block.id, name: block.name };
    const entry = REGISTRY[block.name];
    if (!entry) {
        return { result: toolResult(block.id, `Unknown tool "${block.name}".`, true), activity: { ...base, status: "error", error: "unknown_tool" } };
    }

    const errors = validateInput(TOOL_SCHEMAS[block.name], block.input ?? {});
    if (errors.length) {
        const message = `Invalid input: ${errors.join("; ")}.`;
        return { result: toolResult(block.id, message, true), activity: { ...base, status: "error", error: message } };
    }

    try {
        if (entry.kind !== "write") {
            const outcome = entry.run(block.input, ctx);
            if (outcome.error) {
                return { result: toolResult(block.id, outcome.error, true), activity: { ...base, status: "error", error: outcome.error } };
            }
            return { result: toolResult(block.id, outcome.content), activity: { ...base, ...outcome.activity, status: "done" } };
        }

        // Block manual edits to the group before planning, so nothing changes
        // between the dry run, the approval and the commit.
        const target = ctx.getGroups()[block.input.group - 1];
        if (target && !ctx.isLocked(block.input.group - 1)) ctx.lockGroup(target.id);

        let plan = entry.run(block.input, ctx);
        if (plan.error) {
            return { result: toolResult(block.id, plan.error, true), activity: { ...base, status: "error", error: plan.error } };
        }
        let activity = { ...base, ...writeActivity(block.name, plan) };
        if (plan.touched === 0 && plan.settings.length === 0) {
            return { result: toolResult(block.id, `No changes were needed. ${plan.info}`.trim()), activity: { ...activity, status: "noop" } };
        }

        if (needsConfirmation(block.name, plan)) {
            const approved = await confirm({ name: block.name, activity });
            if (!approved) {
                return {
                    result: toolResult(block.id, "The user declined this action. Nothing was changed."),
                    activity: { ...activity, status: "declined" },
                };
            }
            // While the card was open the user may have moved or removed groups,
            // locked this one, or edited review fields (notes, approvals, tags),
            // which stay editable on agent-locked groups. Re-plan against the
            // project as it is now, so the commit neither hits the wrong group
            // nor reverts those edits.
            const index = ctx.getGroups().findIndex((group) => group.id === plan.groupId);
            if (index === -1) {
                const message = `Group "${plan.groupName}" was removed before the change was applied. Nothing was changed.`;
                return { result: toolResult(block.id, message, true), activity: { ...activity, status: "error", error: message } };
            }
            if (ctx.isLocked(index)) {
                const message = `Group ${index + 1} "${plan.groupName}" was locked before the change was applied. Nothing was changed.`;
                return { result: toolResult(block.id, message, true), activity: { ...activity, status: "error", error: message } };
            }
            const fresh = entry.run({ ...block.input, group: index + 1 }, ctx);
            if (fresh.error) {
                return { result: toolResult(block.id, fresh.error, true), activity: { ...activity, status: "error", error: fresh.error } };
            }
            plan = fresh;
            activity = { ...base, ...writeActivity(block.name, plan) };
        }

        ctx.commit(plan);
        return { result: toolResult(block.id, summarizePlan(plan)), activity: { ...activity, status: "done" } };
    } catch (error) {
        console.error(`Agent tool ${block.name} failed`, error);
        const message = `The tool failed: ${error?.message ?? error}. Nothing was changed.`;
        return { result: toolResult(block.id, message, true), activity: { ...base, status: "error", error: message } };
    }
}
